import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';

function Articles() {
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('alle');
  const [articlesData, setArticlesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/articles').then(res => res.json()).then(data => {
      setArticlesData(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const allTags = useMemo(() => {
    const tags = new Set();
    articlesData.forEach(a => a.tags?.forEach(tag => tags.add(tag)));
    return ['alle', ...Array.from(tags).sort()];
  }, [articlesData]);

  const filteredArticles = useMemo(() => {
    return articlesData
      .filter(a => a.published)
      .filter(a => {
        const q = searchQuery.toLowerCase();
        if (!q) return true;
        return (
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.tags?.some(tag => tag.toLowerCase().includes(q))
        );
      })
      .filter(a => {
        if (selectedTag === 'alle') return true;
        return a.tags?.includes(selectedTag);
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id);
  }, [searchQuery, selectedTag, articlesData]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const bg = darkMode ? 'bg-slate-900' : 'bg-stone-50';
  const headingColor = darkMode ? 'text-white' : 'text-stone-900';
  const subColor = darkMode ? 'text-gray-400' : 'text-stone-500';
  const cardBg = darkMode ? 'bg-slate-800 bg-opacity-60 border-slate-700' : 'bg-white border-stone-200 shadow-sm';
  const cardHover = darkMode ? 'hover:border-teal-500 hover:border-opacity-50 hover:shadow-teal-500/5' : 'hover:border-teal-500 hover:shadow-md';
  const titleColor = darkMode ? 'text-white group-hover:text-teal-400' : 'text-stone-900 group-hover:text-teal-600';
  const excerptColor = darkMode ? 'text-gray-400' : 'text-stone-600';
  const metaColor = darkMode ? 'text-gray-500' : 'text-stone-400';
  const inputBg = darkMode ? 'bg-slate-800 border-slate-700 text-gray-200 placeholder-gray-500' : 'bg-white border-stone-300 text-stone-800 placeholder-stone-400';
  const tagBg = darkMode ? 'bg-teal-500 bg-opacity-15 text-teal-400' : 'bg-teal-50 text-teal-700';
  const tagBtnActive = darkMode ? 'bg-teal-500 text-slate-900' : 'bg-teal-600 text-white';
  const tagBtnInactive = darkMode ? 'bg-slate-800 text-gray-400 hover:bg-slate-700 hover:text-gray-200' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700';
  return (
    <div className={`min-h-screen pt-20 sm:pt-24 pb-12 px-4 transition-colors duration-300 ${bg}`}>
      <SEO
        title="Artikler om elbil"
        path="/artikler"
        description="Les artikler om elbiler, rekkeviddetester, lading og tips for elbileiere i Norge. Oppdatert innsikt fra Effekt-teamet."
        jsonLd={filteredArticles.length > 0 ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Artikler om elbil',
          itemListOrder: 'https://schema.org/ItemListOrderDescending',
          numberOfItems: filteredArticles.length,
          itemListElement: filteredArticles.slice(0, 20).map((a, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://effektnorge.no/artikler/${a.slug}`,
            name: a.title
          }))
        } : undefined}
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className={`text-4xl sm:text-5xl font-bold mb-3 ${headingColor}`} style={{ fontFamily: "'Lora', serif" }}>
            Artikler
          </h1>
          <p className={`text-lg ${subColor}`} style={{ fontFamily: "'Lora', serif" }}>
            Innsikt, tester og nyheter om elbiler
          </p>
        </div>

        {/* Search & Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <svg className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${metaColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Sok i artikler..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors ${inputBg}`}
              style={{ fontFamily: "'Lora', serif" }}
            />
          </div>

          {allTags.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedTag === tag ? tagBtnActive : tagBtnInactive
                  }`}
                >
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results count */}
        {!loading && (
          <p className={`text-sm mb-6 ${metaColor}`}>
            {filteredArticles.length} {filteredArticles.length === 1 ? 'artikkel' : 'artikler'}
          </p>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className={`rounded-2xl p-6 sm:p-8 border animate-pulse ${cardBg}`}>
                <div className={`w-full h-48 rounded-xl mb-5 ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
                <div className="flex gap-2 mb-3">
                  <div className={`h-5 w-16 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
                  <div className={`h-5 w-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
                </div>
                <div className={`h-7 w-3/4 rounded mb-3 ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
                <div className={`h-4 w-full rounded mb-2 ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
                <div className={`h-4 w-2/3 rounded ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
              </div>
            ))}
          </div>
        )}

        {/* Articles list */}
        {!loading && (
          <div className="space-y-6">
            {filteredArticles.map((article, index) => (
              <Link
                key={article.id}
                to={`/artikler/${article.slug}`}
                className="block group"
                style={{ animation: `fadeInUp 0.4s ease-out ${index * 0.08}s both` }}
              >
                <article className={`backdrop-blur-sm border rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:shadow-lg ${cardBg} ${cardHover}`}>
                  {article.coverImage && (
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-48 sm:h-56 object-cover rounded-xl mb-5"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  )}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {article.tags?.slice(0, 2).map(tag => (
                      <Link key={tag} to={`/merke/${encodeURIComponent(tag)}`} className={`px-2.5 py-0.5 text-xs rounded-full font-medium transition-opacity hover:opacity-80 ${tagBg}`}>
                        {tag}
                      </Link>
                    ))}
                  </div>
                  <h2
                    className={`text-xl sm:text-2xl font-bold mb-2 transition-colors ${titleColor}`}
                    style={{ fontFamily: "'Lora', serif" }}
                  >
                    {article.title}
                  </h2>
                  <p className={`mb-4 leading-relaxed ${excerptColor}`} style={{ fontFamily: "'Lora', serif" }}>
                    {article.excerpt}
                  </p>
                  <div className={`flex items-center justify-between text-sm ${metaColor}`}>
                    <span>{article.author}</span>
                    <span>{formatDate(article.createdAt)}</span>
                  </div>
                </article>
              </Link>
            ))}

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <p className={`text-lg ${metaColor}`}>Ingen artikler funnet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default Articles;
